import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '../components/Icon'
import { languageFromPath, withLang } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

// About page — content lives in src/i18n/locales/{lang}/about.json
// so the page is fully bilingual. JSX structure mirrors the FAQ + HIW
// visual language (numbered stamps, alternating bg, dark CTA card).

interface BulletData {
  title: string
  body: string
}

export default function About() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('about')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.title'), description: t('seo.description') })

  // Each "deep section" is data-driven so we can render the loop once.
  const sections = [
    { stamp: 'n1' as const, eyebrow: t('section1.eyebrow'), title: t('section1.title'), body: t('section1.body') },
    { stamp: 'n2' as const, eyebrow: t('section2.eyebrow'), title: t('section2.title'), body: t('section2.body') },
    {
      stamp: 'n3' as const,
      eyebrow: t('section3.eyebrow'),
      title: t('section3.title'),
      bullets: t('section3.bullets', { returnObjects: true }) as BulletData[],
    },
    { stamp: 'n1' as const, eyebrow: t('section4.eyebrow'), title: t('section4.title'), body: t('section4.body') },
  ]

  return (
    <div className="faq">
      <section className="faq-hero">
        <div className="faq-hero-inner">
          <div className="breadcrumb">
            <Link to={withLang('/', lang)} style={{ color: 'var(--ink-3)' }}>
              {t('nav.home', { ns: 'common' })}
            </Link>{' '}
            / <span style={{ color: 'var(--ink)' }}>{t('breadcrumb')}</span>
          </div>
          <div className="eyebrow" style={{ marginTop: 16 }}>{t('hero.eyebrow')}</div>
          <h1 className="faq-hero-h1">
            {t('hero.titlePart1')}<em>{t('hero.titleEmphasis')}</em>
          </h1>
          <p className="lede faq-hero-lede">{t('hero.lede')}</p>
        </div>
      </section>

      {sections.map((s, idx) => (
        <section
          key={idx}
          className="section how-deep faq-deep"
          style={{ background: idx % 2 === 0 ? 'var(--bg-sunk)' : 'var(--bg)' }}
        >
          <div className="section-inner">
            <div className="how-deep-head">
              <div className={`how-deep-stamp ${s.stamp}`}>0{idx + 1}</div>
              <div>
                <div className="eyebrow" style={{ marginBottom: 8 }}>{s.eyebrow}</div>
                <h2 className="h1">{s.title}</h2>
              </div>
            </div>

            <div className="about-section-body">
              {s.body && <p className="about-body">{s.body}</p>}

              {s.bullets && (
                <ul className="about-list">
                  {s.bullets.map((b) => (
                    <li key={b.title}>
                      <strong>{b.title}.</strong> {b.body}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      ))}

      <section className="section how-cta">
        <div className="section-inner">
          <div className="how-cta-card faq-cta-card">
            <div className="how-cta-body" style={{ padding: '56px' }}>
              <div className="eyebrow how-cta-eyebrow">{t('cta.eyebrow')}</div>
              <h2 className="h1 how-cta-h1">
                {t('cta.titlePart1')}<em>{t('cta.titleEmphasis')}</em>
              </h2>
              <p className="lede how-cta-lede">{t('cta.lede')}</p>
              <div className="how-cta-row">
                <button
                  type="button"
                  className="btn primary lg"
                  onClick={() => navigate(withLang('/destinations', lang))}
                >
                  {t('cta.primary')} <Icon name="arrow" size={14} />
                </button>
                <Link to={withLang('/how-it-works', lang)} className="btn ghost lg">
                  {t('cta.ghost')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
